import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const MESES_PT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function nowRefMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
}
function nowRefTrim() {
  const d = new Date();
  const q = Math.floor(d.getMonth()/3) + 1;
  return `${d.getFullYear()}-Q${q}`;
}

function Arrow({ v }) {
  if (v === null || v === undefined) return <span className="text-slate-400 text-xs">—</span>;
  if (v > 0) return <span className="text-emerald-700 text-xs font-semibold" data-testid="arrow-up">↑ {Math.abs(v).toFixed(1)}%</span>;
  if (v < 0) return <span className="text-rose-700 text-xs font-semibold" data-testid="arrow-down">↓ {Math.abs(v).toFixed(1)}%</span>;
  return <span className="text-slate-500 text-xs">= 0%</span>;
}

function Card({ titulo, valor, variacao, testid }) {
  const display = valor === null || valor === undefined ? "—" : (typeof valor === "number" ? (Number.isInteger(valor) ? valor : valor.toFixed(2)) : valor);
  return (
    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm" data-testid={testid}>
      <div className="text-xs text-slate-500 font-medium">{titulo}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-2xl font-extrabold text-slate-900">{display}</div>
        <Arrow v={variacao} />
      </div>
    </div>
  );
}

export default function RelatorioAvaliacoes() {
  const navigate = useNavigate();
  const [tipo, setTipo] = useState("mes");
  const [ref, setRef] = useState(nowRefMes());
  const [drillStack, setDrillStack] = useState([]); // [{id,nome,nivel}]
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const drill = drillStack[drillStack.length - 1]?.id || null;

  const trocarTipo = (t) => {
    setTipo(t);
    setRef(t === "mes" ? nowRefMes() : nowRefTrim());
    setDrillStack([]);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get("/canal-cliente/relatorio/avaliacoes", { params: { tipo, ref, drill: drill || undefined } });
      setData(r.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar avaliações");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [tipo, ref, drill]);

  // Opções de ref (últimos 12 meses / 8 trimestres).
  const opcoes = useMemo(() => {
    const out = [];
    const d = new Date();
    if (tipo === "mes") {
      for (let i = 0; i < 12; i++) {
        const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
        const v = `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`;
        out.push({ value: v, label: `${MESES_PT[dt.getMonth()]}/${dt.getFullYear()}` });
      }
    } else {
      const curQ = Math.floor(d.getMonth()/3) + 1;
      let y = d.getFullYear(), q = curQ;
      for (let i = 0; i < 8; i++) {
        out.push({ value: `${y}-Q${q}`, label: `T${q}/${y}` });
        q -= 1; if (q === 0) { q = 4; y -= 1; }
      }
    }
    return out;
  }, [tipo]);

  const atual = data?.atual || {};
  const varPct = data?.variacoes || {};
  const ranking = data?.ranking?.linhas || [];
  const nivelLabel = { vendedor: "Vendedores", gerente: "Gerentes", diretor: "Diretores" }[data?.ranking?.nivel] || "Ranking";

  const abrirDrill = (linha) => {
    if (data?.ranking?.nivel === "vendedor") return; // último nível
    setDrillStack((s) => [...s, { id: linha.id, nome: linha.nome, nivel: data.ranking.nivel }]);
  };
  const voltarDrill = () => setDrillStack((s) => s.slice(0, -1));

  return (
    <div className="space-y-4" data-testid="rel-avaliacoes">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden">
          <button onClick={() => trocarTipo("mes")} data-testid="filtro-mes"
            className={`px-3 py-1.5 text-sm font-medium ${tipo === "mes" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}>Mês</button>
          <button onClick={() => trocarTipo("trimestre")} data-testid="filtro-trim"
            className={`px-3 py-1.5 text-sm font-medium ${tipo === "trimestre" ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}>Trimestre</button>
        </div>
        <select value={ref} onChange={(e) => { setRef(e.target.value); setDrillStack([]); }} data-testid="filtro-ref"
          className="h-9 px-2 border border-slate-300 rounded-lg text-sm bg-white">
          {opcoes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <div className="text-sm text-slate-500">
          {data?.periodo?.label}
          {data?.anterior?.label && <span className="ml-1 text-xs">(vs {data.anterior.label})</span>}
        </div>
      </div>

      {/* Indicadores */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card titulo="Treinamentos aplicados" valor={atual.aplicados} variacao={varPct.aplicados} testid="ind-aplicados" />
        <Card titulo="Respostas" valor={atual.respostas} variacao={varPct.respostas} testid="ind-respostas" />
        <Card titulo="Nota do treinamento" valor={atual.media_treinamento} variacao={varPct.media_treinamento} testid="ind-trein" />
        <Card titulo="Atendimento MPires" valor={atual.media_atendimento_mpires} variacao={varPct.media_atendimento_mpires} testid="ind-mpires" />
        <Card titulo="Técnico" valor={atual.media_atendimento_tecnico} variacao={varPct.media_atendimento_tecnico} testid="ind-tecnico" />
        <Card titulo="Vendedor" valor={atual.media_atendimento_vendedor} variacao={varPct.media_atendimento_vendedor} testid="ind-vend" />
        <Card titulo="Gerente" valor={atual.media_atendimento_gerente} variacao={varPct.media_atendimento_gerente} testid="ind-ger" />
      </div>

      {/* Ranking (ou cartão da posição do vendedor) */}
      {data?.ranking?.oculto ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="rk-vendedor-card">
          <div className="text-sm font-bold text-slate-900">Sua posição no ranking da equipe</div>
          {(() => {
            const mp = data?.ranking?.minha_posicao || {};
            if (mp.amostra_pequena || !mp.posicao) {
              return <div className="mt-1 text-slate-700 text-base">Amostra pequena neste período (menos de 5 respostas).</div>;
            }
            return <div className="mt-1 text-slate-900 text-2xl font-extrabold">{mp.posicao}º de {mp.total_no_ranking} vendedores da equipe</div>;
          })()}
          <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-slate-500 text-xs">Seus treinamentos aplicados</div><div className="text-slate-900 font-semibold">{ranking[0]?.aplicados ?? 0}</div></div>
            <div><div className="text-slate-500 text-xs">Suas respostas</div><div className="text-slate-900 font-semibold">{ranking[0]?.respostas ?? 0}</div></div>
            <div><div className="text-slate-500 text-xs">Sua nota de treinamento</div><div className="text-slate-900 font-semibold">{ranking[0]?.media_treinamento ?? "—"}</div></div>
            <div><div className="text-slate-500 text-xs">Sua nota de atendimento</div><div className="text-slate-900 font-semibold">{ranking[0]?.media_atendimento ?? "—"}</div></div>
          </div>
        </div>
      ) : (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div>
            <div className="text-sm font-bold text-slate-900">Avaliação feita pelos clientes · {nivelLabel}</div>
            {drillStack.length > 0 && (
              <div className="text-xs text-slate-500 mt-0.5">{drillStack.map((d) => d.nome).join(" › ")}</div>
            )}
          </div>
          {drillStack.length > 0 && (
            <button onClick={voltarDrill} data-testid="btn-voltar-drill" className="text-sm text-slate-700 border border-slate-300 rounded-md px-3 py-1 hover:bg-slate-50">← Voltar</button>
          )}
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500 text-sm">Carregando…</div>
        ) : ranking.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">Sem dados no período.</div>
        ) : (
          <>
            {/* Tabela em >=md */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 w-10">#</th>
                    <th className="text-left px-3 py-2">Nome</th>
                    <th className="text-right px-3 py-2">Treinamentos</th>
                    <th className="text-right px-3 py-2">Respostas</th>
                    <th className="text-right px-3 py-2">Nota treinamento</th>
                    <th className="text-right px-3 py-2">Nota atendimento</th>
                    <th className="text-right px-3 py-2">Variação</th>
                  </tr>
                </thead>
                <tbody>
                  {ranking.map((l) => {
                    const drillable = data.ranking.nivel !== "vendedor";
                    return (
                      <tr key={l.id} className={`border-t border-slate-100 ${drillable ? "cursor-pointer hover:bg-slate-50" : ""}`}
                        onClick={() => drillable && abrirDrill(l)} data-testid={`rk-row-${l.id}`}>
                        <td className="px-3 py-2 text-slate-500">{l.posicao ? l.posicao : "–"}</td>
                        <td className="px-3 py-2 font-medium text-slate-900">
                          {l.nome}
                          {l.amostra_pequena && <span className="ml-2 text-xs text-slate-500 font-normal">(amostra pequena)</span>}
                        </td>
                        <td className="px-3 py-2 text-right">{l.aplicados}</td>
                        <td className="px-3 py-2 text-right">{l.respostas}</td>
                        <td className="px-3 py-2 text-right">{l.media_treinamento ?? "—"}</td>
                        <td className="px-3 py-2 text-right">{l.media_atendimento ?? "—"}</td>
                        <td className="px-3 py-2 text-right"><Arrow v={l.var_atendimento_pct} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Cards no celular */}
            <div className="md:hidden divide-y divide-slate-100">
              {ranking.map((l) => {
                const drillable = data.ranking.nivel !== "vendedor";
                return (
                  <div key={l.id} className="p-3" onClick={() => drillable && abrirDrill(l)} data-testid={`rk-card-${l.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 w-6">{l.posicao ? `#${l.posicao}` : "–"}</span>
                        <span className="font-semibold text-slate-900">{l.nome}</span>
                      </div>
                      <Arrow v={l.var_atendimento_pct} />
                    </div>
                    {l.amostra_pequena && <div className="text-xs text-slate-500 mt-0.5">amostra pequena</div>}
                    <div className="grid grid-cols-2 gap-y-1 gap-x-4 mt-2 text-xs text-slate-600">
                      <div>Treinamentos: <span className="text-slate-900 font-semibold">{l.aplicados}</span></div>
                      <div>Respostas: <span className="text-slate-900 font-semibold">{l.respostas}</span></div>
                      <div>Nota treinamento: <span className="text-slate-900 font-semibold">{l.media_treinamento ?? "—"}</span></div>
                      <div>Nota atendimento: <span className="text-slate-900 font-semibold">{l.media_atendimento ?? "—"}</span></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
        {data?.ranking?.minha_posicao != null && typeof data.ranking.minha_posicao === "number" && (
          <div className="px-4 py-2 border-t border-slate-100 text-sm text-slate-700">
            Sua posição no ranking da equipe: <span className="font-bold">#{data.ranking.minha_posicao}</span>
          </div>
        )}
      </div>
      )}

      {/* Notas baixas */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-bold text-slate-900">Notas baixas no período (1 e 2)</div>
        {!data?.notas_baixas?.length ? (
          <div className="p-6 text-center text-slate-500 text-sm">Nenhuma nota baixa.</div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2">Data</th>
                    <th className="text-left px-3 py-2">Cliente</th>
                    <th className="text-left px-3 py-2">Tema</th>
                    <th className="text-left px-3 py-2">Aplicou</th>
                    <th className="text-right px-3 py-2 w-14">Nota</th>
                    <th className="text-left px-3 py-2">Comentário</th>
                  </tr>
                </thead>
                <tbody>
                  {data.notas_baixas.map((n, i) => (
                    <tr key={i} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/cliente/${n.cliente_id}`)} data-testid={`nota-baixa-${i}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{n.data?.split("-").reverse().join("/")}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{n.cliente_nome}</td>
                      <td className="px-3 py-2">{n.tema}</td>
                      <td className="px-3 py-2">{n.aplicado_por_nome}</td>
                      <td className="px-3 py-2 text-right font-bold text-rose-700">{n.nota_geral}</td>
                      <td className="px-3 py-2 text-slate-600">{n.comentario || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-slate-100">
              {data.notas_baixas.map((n, i) => (
                <div key={i} className="p-3" onClick={() => navigate(`/cliente/${n.cliente_id}`)} data-testid={`nota-baixa-card-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-slate-900">{n.cliente_nome}</div>
                    <div className="text-rose-700 font-bold text-sm">Nota {n.nota_geral}</div>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{n.data?.split("-").reverse().join("/")} · {n.tema}</div>
                  <div className="text-xs text-slate-600 mt-1">Aplicou: {n.aplicado_por_nome}</div>
                  {n.comentario && <div className="text-sm text-slate-700 mt-1">{n.comentario}</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
